import React, { useState, useMemo, useCallback } from 'react'
import { Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Truck, Clock, IndianRupee, Users } from 'lucide-react'
import { useData } from '../context/DataContext'
import CustomerForm from '../components/CustomerForm'
import Modal from '../components/Modal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'
import { simpleDbTest, testSimpleCustomerCreate } from '../utils/simpleDbTest'

function Customers() {
  console.log('Customers component is loading...')
  const { state, addCustomer, updateCustomer, deleteCustomer, refreshData } = useData()
  const { customers, loading, error } = state

  console.log('Customers state:', customers)
  console.log('Loading:', loading)
  console.log('Error:', error)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, customer: null })

  // Memoized filtered customers for better performance
  const filteredCustomers = useMemo(() => {
    if (!searchTerm.trim()) return customers
    
    const term = searchTerm.toLowerCase()
    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(term) ||
      customer.phone?.includes(term) ||
      customer.email?.toLowerCase().includes(term) ||
      customer.company?.toLowerCase().includes(term)
    )
  }, [customers, searchTerm])

  // Memoized handlers to prevent unnecessary re-renders
  const handleAddCustomer = useCallback(() => {
    setEditingCustomer(null)
    setIsModalOpen(true)
  }, [])

  const handleEditCustomer = useCallback((customer) => {
    setEditingCustomer(customer)
    setIsModalOpen(true)
  }, [])

  const handleDeleteCustomer = useCallback(async (customer) => {
    const result = await deleteCustomer(customer.id)
    if (!result.success) {
      alert(`Error deleting customer: ${result.error}`)
    }
  }, [deleteCustomer])

  const openDeleteModal = useCallback((customer) => {
    setDeleteModal({ isOpen: true, customer })
  }, [])

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false, customer: null })
  }, [])

  const confirmDelete = useCallback(() => {
    if (deleteModal.customer) {
      handleDeleteCustomer(deleteModal.customer)
    }
  }, [deleteModal.customer, handleDeleteCustomer])

  const handleSubmit = useCallback(async (customerData) => {
    try {
      if (editingCustomer) {
        const result = await updateCustomer(editingCustomer.id, customerData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingCustomer(null)
        } else {
          alert(`Error updating customer: ${result.error}`)
        }
      } else {
        const result = await addCustomer(customerData)
        if (result.success) {
          setIsModalOpen(false)
          setEditingCustomer(null)
        } else {
          console.error('Customer creation failed:', result.error)
          alert(`Error creating customer: ${result.error}\n\nPlease check:\n1. You are logged in\n2. Database connection is working\n3. Required fields are filled`)
        }
      }
    } catch (error) {
      console.error('Error submitting customer:', error)
      alert('An error occurred while saving the customer. Please try again.')
    }
  }, [editingCustomer, addCustomer, updateCustomer])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setEditingCustomer(null)
  }, [])

  // Show loading state during operations
  const isUpdating = loading && editingCustomer
  const isCreating = loading && !editingCustomer && isModalOpen

  const handleTestDatabase = useCallback(async () => {
    console.log('Testing database connection...')
    const result = await simpleDbTest()
    console.log('Database test result:', result)
    
    if (result.success) {
      alert(`✅ Database connection successful!\n\n${result.message}`)
    } else {
      alert(`❌ Database connection failed:\n\nError: ${result.error}\nCode: ${result.code || 'N/A'}`)
    }
  }, [])

  const handleTestCustomerCreation = useCallback(async () => {
    console.log('Testing customer creation...')
    const testData = {
      name: 'Test Customer',
      phone: '1234567890',
      address: 'Test Address',
      city: 'Test City',
      state: 'Test State',
      pincode: '123456'
    }
    
    const result = await testSimpleCustomerCreate(testData)
    console.log('Customer creation test result:', result)
    
    if (result.success) {
      alert(`✅ Customer creation test successful!\n\n${result.message}`)
    } else {
      alert(`❌ Customer creation test failed:\n\nError: ${result.error}\nCode: ${result.code || 'N/A'}`)
    }
  }, [])

  // Memoized search handler with debouncing
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value)
  }, [])

  return (
    <div className="container mx-auto px-4 py-2">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Customers</h1>
          <p className="text-gray-600">Manage your customer information and contacts</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddCustomer}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Customer</span>
          </button>
        </div>
      </div>

      {/* Total Customers Card */}
      <div className="mb-6">
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-base sm:text-lg font-semibold text-gray-900">{customers.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4 space-y-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers by name, phone, email, or company..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="input pl-10"
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-red mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading customers...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">Error: {error}</p>
        </div>
      )}

      {/* Customer List */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.length > 0 ? (
            filteredCustomers.map(customer => (
              <div key={customer.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                {/* Customer Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {customer.name}
                    </h3>
                    {customer.company && (
                      <p className="text-sm text-gray-600 mb-2">{customer.company}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditCustomer(customer)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Edit customer"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {/* Delete button hidden */}
                    {/* <button
                      onClick={() => openDeleteModal(customer)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Delete customer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button> */}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-2 mb-4">
                  {customer.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{customer.email}</span>
                    </div>
                  )}
                  {customer.address && (
                    <div className="flex items-start text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                      <span>{customer.address}</span>
                    </div>
                  )}
                </div>

                {/* Location Details */}
                {(customer.city || customer.state || customer.pincode) && (
                  <div className="text-xs text-gray-500 mb-4">
                    {[customer.city, customer.state, customer.pincode].filter(Boolean).join(', ')}
                  </div>
                )}

                {/* Customer Info */}
                <div className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                  <div>Created: {new Date(customer.created_at || customer.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full">
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-4">
                  <Plus className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No customers found' : 'No customers yet'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm 
                    ? 'Try adjusting your search criteria' 
                    : 'Get started by adding your first customer'
                  }
                </p>
                {!searchTerm && (
                  <button
                    onClick={handleAddCustomer}
                    className="btn btn-primary"
                  >
                    Add Your First Customer
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customer Form Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        size="max-w-4xl"
      >
        <CustomerForm
          customer={editingCustomer}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isSubmitting={isUpdating || isCreating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${deleteModal.customer?.name}"?`}
        itemName={deleteModal.customer?.name || 'Customer'}
      />
    </div>
  )
}

export default Customers
